/**
 * Lo stage designer, a schermo pieno.
 *
 * **Perché una finestra a sé e non un riquadro dentro il form.** Il planner
 * viveva dentro la modale "Aggiungi esercizio", che scrolla, con un proprio
 * contenitore scrollabile e dentro ancora un SVG che cattura il tocco: tre
 * livelli, e sul telefono il browser sbagliava sistematicamente a indovinare
 * quale dovesse ricevere il gesto. Tolto un livello, il problema sparisce —
 * e disegnare una piantina su mezzo schermo era comunque un lavoro scomodo.
 *
 * Una sola superficie per tutti i contesti: ProgramBuilder, libreria drill e
 * vista atleta aprono questa, in scrittura o in `readOnly`. Non se ne scrive
 * una seconda — vale la stessa regola di `ConditioningTemplateEditor`.
 *
 * Il salvataggio è **esplicito**: si lavora su una bozza locale e si conferma.
 * Un editor a schermo pieno che scrive a ogni trascinamento non lascia modo di
 * cambiare idea, e l'annulla del planner copre il singolo gesto, non la
 * sessione.
 *
 * Spec: docs/shooting/13-piano-revisione-test-federico.md §Gruppo B
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Download, PencilRuler } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "../ui/dialog";
import { StagePlanner } from "./StagePlanner";
import { StageShape } from "./StageShapes";
import { elementSpec, groupTargets, requiredShots } from "./stageElements";

/**
 * L'SVG del canvas come PNG.
 *
 * Serve a due cose: l'immagine del drill che il coach può caricare al posto
 * del disegno, e il foglio stampabile. Si fa a mano con un `Image` su canvas —
 * nessuna libreria, e l'SVG è già tutto inline.
 */
export const stageSvgToPngBlob = (svg, scale = 2) =>
	new Promise((resolve, reject) => {
		if (!svg) return reject(new Error("Nessuna piantina da esportare"));
		const box = svg.viewBox?.baseVal;
		const width = Math.round((box?.width || svg.clientWidth || 800) * scale);
		const height = Math.round((box?.height || svg.clientHeight || 600) * scale);

		const clone = svg.cloneNode(true);
		clone.setAttribute("width", width);
		clone.setAttribute("height", height);
		const source = new XMLSerializer().serializeToString(clone);
		// `data:` e non `blob:`: un blob URL su un SVG che referenzia marker
		// interni viene bloccato come risorsa esterna in alcuni browser.
		const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(source)}`;

		const image = new Image();
		image.onload = () => {
			const canvas = document.createElement("canvas");
			canvas.width = width;
			canvas.height = height;
			const ctx = canvas.getContext("2d");
			// Fondo bianco esplicito: il canvas nasce trasparente e un PNG
			// trasparente stampato diventa un foglio nero.
			ctx.fillStyle = "#ffffff";
			ctx.fillRect(0, 0, width, height);
			ctx.drawImage(image, 0, 0, width, height);
			canvas.toBlob(
				(blob) =>
					blob ? resolve(blob) : reject(new Error("Esportazione fallita")),
				"image/png",
			);
		};
		image.onerror = () => reject(new Error("Esportazione fallita"));
		image.src = url;
	});

export function StageDesignerDialog({
	open,
	onOpenChange,
	prescription,
	onSave,
	plannedRounds,
	discipline = "ipsc",
	readOnly = false,
	title,
	onExportPng,
}) {
	const [draft, setDraft] = useState(prescription || {});
	const svgRef = useRef(null);
	const [exporting, setExporting] = useState(false);

	// La bozza riparte dalla prescrizione a ogni apertura: chiudere senza
	// salvare deve davvero buttare via le modifiche.
	useEffect(() => {
		if (open) setDraft(prescription || {});
	}, [open, prescription]);

	const captureSvg = useCallback((ref) => {
		svgRef.current = ref?.current || null;
	}, []);

	const handleExport = async () => {
		setExporting(true);
		try {
			const blob = await stageSvgToPngBlob(svgRef.current);
			await onExportPng?.(blob);
		} catch (error) {
			toast.error(error.message);
		} finally {
			setExporting(false);
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			{/* Schermo pieno e **un solo** contenitore: il canvas prende lo
			    spazio che resta, niente scrolla. */}
			<DialogContent className="bg-card border-border rounded-sm w-screen max-w-none h-[100dvh] max-h-[100dvh] sm:w-[calc(100vw-2rem)] sm:max-w-6xl sm:h-[calc(100dvh-2rem)] p-4 flex flex-col gap-2 overflow-hidden">
				<DialogHeader className="shrink-0 pr-8">
					<DialogTitle className="font-heading flex items-center gap-2 text-base">
						<PencilRuler className="w-4 h-4 text-volt" />
						{title || (readOnly ? "Piantina dello stage" : "Disegna lo stage")}
					</DialogTitle>
					<DialogDescription className="text-xs">
						{readOnly
							? "Vista dall'alto, in scala. I bersagli sono disegnati di fronte per riconoscerli."
							: "Scegli uno strumento e posalo sul campo. Trascina per spostare, Canc elimina, R ruota."}
					</DialogDescription>
				</DialogHeader>

				<div className="flex-1 min-h-0">
					<StagePlanner
						prescription={draft}
						onChange={setDraft}
						plannedRounds={plannedRounds}
						discipline={discipline}
						readOnly={readOnly}
						onRequestSvg={captureSvg}
					/>
				</div>

				<div className="shrink-0 flex items-center gap-2 pt-1">
					{onExportPng && (
						<Button
							type="button"
							variant="outline"
							size="sm"
							className="rounded-sm"
							onClick={handleExport}
							disabled={exporting}
						>
							<Download className="w-3.5 h-3.5 mr-1.5" />
							{exporting ? "Esporto..." : "Genera immagine"}
						</Button>
					)}
					<div className="flex-1" />
					<Button
						type="button"
						variant="ghost"
						size="sm"
						className="rounded-sm"
						onClick={() => onOpenChange?.(false)}
					>
						{readOnly ? "Chiudi" : "Annulla"}
					</Button>
					{!readOnly && (
						<Button
							type="button"
							size="sm"
							className="rounded-sm bg-volt hover:bg-volt/90"
							onClick={() => {
								onSave?.(draft);
								onOpenChange?.(false);
							}}
						>
							Salva piantina
						</Button>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
}

/**
 * Anteprima in riga della piantina: miniatura statica, click per aprire.
 *
 * Esiste perché nel form non deve più comparire un editor — comparirebbe un
 * contenitore scrollabile dentro un altro, che è il difetto che il dialog
 * risolve. Qui si vede **cosa c'è** e si entra a schermo pieno per lavorarci.
 */
export function StagePreview({ prescription = {}, onOpen, className = "" }) {
	const targets = prescription.targets || [];
	const layout = prescription.stage_layout || [];
	if (targets.length === 0 && layout.length === 0) return null;

	const widthM = prescription.stage_width_m || 20;
	const depthM = prescription.stage_depth_m || 25;
	const PX = 10;
	const summary = groupTargets(targets)
		.map((t) => `${t.count > 1 ? `${t.count}× ` : ""}${elementSpec(t.target_type).label}`)
		.join(" · ");

	return (
		<button
			type="button"
			onClick={onOpen}
			className={`w-full rounded-sm border border-border bg-white overflow-hidden text-left hover:border-volt/50 transition-colors ${className}`}
		>
			<svg
				viewBox={`0 0 ${widthM * PX} ${depthM * PX}`}
				className="w-full block"
				style={{ maxHeight: "11rem" }}
				preserveAspectRatio="xMidYMid meet"
			>
				<rect width={widthM * PX} height={depthM * PX} fill="#ffffff" />
				{[...layout, ...targets].map((item, index) => {
					const type = item.target_type || item.element_type;
					const spec = elementSpec(type);
					const magnify = spec.group === "target" ? 2.4 : 1;
					const w = (item.length_m || spec.widthM) * PX * magnify;
					const h = spec.heightM * PX * magnify;
					const x = ((item.x_m ?? 0) + widthM / 2) * PX;
					const y = (depthM - (item.y_m ?? 0)) * PX;
					return (
						<g
							key={index}
							transform={`translate(${x - w / 2}, ${
								spec.group === "target" ? y - h : y - h / 2
							})`}
						>
							<StageShape type={type} widthPx={w} heightPx={h} />
						</g>
					);
				})}
			</svg>
			<p className="text-[11px] text-muted-foreground px-2 py-1.5 truncate">
				{summary || "Piantina"}
				{targets.length > 0 ? ` · ${requiredShots(targets)} colpi per serie` : ""}
			</p>
		</button>
	);
}
